import {ChevronDown} from 'lucide-react'
import React, {useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react'
import {createPortal} from 'react-dom'

export interface FilterSelectOption {
  value: string
  label: string
  /** Small image (e.g. a flag) shown before the label in the menu and trigger. */
  iconUrl?: string
}

interface Props {
  value: string
  options: readonly FilterSelectOption[]
  onChange: (value: string) => void
  /** Extra classes for the trigger, on top of `filter-select-trigger`. */
  triggerClassName?: string
  /** Extra classes for the wrapper, on top of `filter-select-wrapper`. */
  className?: string
  ariaLabel?: string
  /** Put on the trigger, so a visible <label htmlFor> can point at it. */
  id?: string
  /** Greys the trigger out and stops it opening — e.g. while a form is submitting. */
  disabled?: boolean
  /**
   * 'fixed' (default): menu is position-fixed, anchored to the trigger's
   * viewport rect — survives clipping by scrolling containers.
   * 'under': menu is position-absolute directly under the trigger. Use inside
   * animated/transformed containers (e.g. the onboarding wizard steps), where
   * a transformed ancestor re-bases position: fixed and the menu lands away
   * from the trigger.
   */
  placement?: 'fixed' | 'under'
  /**
   * Render the fixed menu into document.body via a portal. Use when the trigger
   * sits inside a transformed ancestor that CANNOT be avoided (e.g. MUI DataGrid
   * rows, which are positioned with translate3d) — there a non-portalled fixed
   * menu is re-based by the transform and lands away from the trigger, while a
   * body-portalled one keeps its viewport anchoring. Ignored for 'under'.
   */
  portal?: boolean
  /**
   * Show a search input at the top of the menu that filters options by label.
   * For long option lists (e.g. countries) where scrolling alone is unusable.
   */
  searchable?: boolean
  /** Already-translated placeholder for the search input. */
  searchPlaceholder?: string
  /** Already-translated text shown when the search matches no options. */
  emptyMessage?: string
}

/**
 * Drop-in replacement for a native <select> used as a filter.
 *
 * Two reasons this exists:
 *  - A native <select> menu is rendered by the OS on mobile (Android shows a
 *    centered dialog, iOS a bottom sheet), so it cannot be positioned and looks
 *    nothing like the surrounding UI.
 *  - The `.form-select` caret is a background image, so it overlaps the label
 *    once the label is wide. Here the caret is a real element in a flex row.
 *
 * The menu is `position: fixed` and anchored to the trigger's viewport rect, so
 * scrolling containers (report bodies, cards) cannot clip it — without that the
 * menu was cut off and the user had to scroll the container to reach the
 * options. The rect is measured on open, so scroll/resize close the menu rather
 * than let it drift away from the trigger.
 *
 * For a form field with validation/multi-select, use `CustomSelect` instead.
 */
/** Widest the menu may get before long labels start wrapping. */
const MAX_MENU_WIDTH = 320
/** Tallest the menu may get before it scrolls internally. */
const MAX_MENU_HEIGHT = 260
/** Gap kept between the menu and the viewport edges. */
const VIEWPORT_MARGIN = 8

const FilterSelect = ({
  value,
  options,
  onChange,
  triggerClassName = '',
  className = '',
  ariaLabel,
  id,
  disabled = false,
  placement = 'fixed',
  portal = false,
  searchable = false,
  searchPlaceholder,
  emptyMessage,
}: Props) => {
  const isUnder = placement === 'under'
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [pos, setPos] = useState<React.CSSProperties | null>(null)
  const rootRef = useRef<HTMLDivElement | null>(null)
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const menuRef = useRef<HTMLDivElement | null>(null)
  const searchRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    if (!open) {
      setSearch('')
      return
    }
    if (searchable) searchRef.current?.focus()
  }, [open, searchable])

  const measure = useCallback(() => {
    const r = triggerRef.current?.getBoundingClientRect()
    if (!r) return
    const vw = window.innerWidth
    const vh = window.innerHeight

    // A left-anchored menu runs off-screen when the trigger sits on the right
    // (filter toolbars are right-aligned), so anchor to whichever edge leaves
    // more room and cap the width against that edge.
    const anchorRight = r.left > vw / 2

    // Triggers near the bottom of the page (a grid's page-size picker sits in
    // the footer) leave no room below, and the menu is fixed — the page cannot
    // be scrolled to reach what spills past the viewport. Flip it above the
    // trigger whenever that side has more room.
    const spaceBelow = vh - r.bottom - 4 - VIEWPORT_MARGIN
    const spaceAbove = r.top - 4 - VIEWPORT_MARGIN
    const dropUp = spaceBelow < MAX_MENU_HEIGHT && spaceAbove > spaceBelow

    setPos({
      ...(dropUp ? {bottom: vh - r.top + 4} : {top: r.bottom + 4}),
      ...(anchorRight ? {right: vw - r.right} : {left: r.left}),
      minWidth: r.width,
      maxWidth: Math.min(MAX_MENU_WIDTH, (anchorRight ? r.right : vw - r.left) - VIEWPORT_MARGIN),
      // Scroll internally past a few options rather than spilling off-screen.
      maxHeight: Math.max(140, Math.min(MAX_MENU_HEIGHT, dropUp ? spaceAbove : spaceBelow)),
    })
  }, [])

  useLayoutEffect(() => {
    if (open && !isUnder) measure()
  }, [open, isUnder, measure])

  // Disabled mid-interaction (a submit started while the menu was open): close it, or the
  // options stay clickable over a greyed-out trigger.
  useEffect(() => {
    if (disabled) setOpen(false)
  }, [disabled])

  useEffect(() => {
    if (!open) return
    const onDocPointerDown = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node
      // A portalled menu lives outside rootRef — treat clicks inside it as inside.
      if (
        rootRef.current &&
        !rootRef.current.contains(target) &&
        !menuRef.current?.contains(target)
      ) {
        setOpen(false)
      }
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    // Capture phase, so scrolling any ancestor container closes the menu (its
    // position is measured once and would otherwise drift). The menu's own
    // overflow scrolling must be exempt, or picking from a long list would
    // dismiss it the moment the user scrolls the options. An 'under' menu moves
    // with the trigger, so it can stay open across scroll/resize.
    const onScroll = (e: Event) => {
      if (menuRef.current?.contains(e.target as Node)) return
      setOpen(false)
    }
    const close = () => setOpen(false)
    document.addEventListener('mousedown', onDocPointerDown)
    document.addEventListener('touchstart', onDocPointerDown)
    document.addEventListener('keydown', onKeyDown)
    if (!isUnder) {
      window.addEventListener('scroll', onScroll, true)
      window.addEventListener('resize', close)
    }
    return () => {
      document.removeEventListener('mousedown', onDocPointerDown)
      document.removeEventListener('touchstart', onDocPointerDown)
      document.removeEventListener('keydown', onKeyDown)
      if (!isUnder) {
        window.removeEventListener('scroll', onScroll, true)
        window.removeEventListener('resize', close)
      }
    }
  }, [open, isUnder])

  const selected = options.find((o) => o.value === value)

  const needle = search.trim().toLowerCase()
  const visibleOptions = needle
    ? options.filter((o) => o.label.toLowerCase().includes(needle))
    : options

  return (
    <div
      className={`filter-select-wrapper${isUnder ? ' filter-select-wrapper-under' : ''} ${className}`.trim()}
      ref={rootRef}
    >
      <button
        ref={triggerRef}
        id={id}
        type='button'
        className={`filter-select-trigger ${triggerClassName}`.trim()}
        aria-haspopup='listbox'
        aria-expanded={open}
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
      >
        {selected?.iconUrl && <img src={selected.iconUrl} alt='' className='filter-select-icon' />}
        <span className='filter-select-value'>{selected?.label ?? value}</span>
        <ChevronDown size={14} className='filter-select-caret' aria-hidden />
      </button>

      {open &&
        (isUnder || pos) &&
        (() => {
          const menu = (
            <div
              className={`filter-select-menu${isUnder ? ' filter-select-menu-under' : ''}`}
              role='listbox'
              style={isUnder ? {maxHeight: MAX_MENU_HEIGHT} : (pos ?? undefined)}
              ref={menuRef}
            >
              {searchable && (
                <div className='filter-select-search'>
                  <input
                    ref={searchRef}
                    type='text'
                    className='filter-select-search-input'
                    value={search}
                    placeholder={searchPlaceholder}
                    aria-label={searchPlaceholder ?? ariaLabel}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
              )}
              {searchable && visibleOptions.length === 0 && emptyMessage && (
                <div className='filter-select-empty'>{emptyMessage}</div>
              )}
              {visibleOptions.map((opt) => (
                <button
                  key={opt.value}
                  type='button'
                  role='option'
                  aria-selected={opt.value === value}
                  className={`filter-select-option${opt.value === value ? ' active' : ''}`}
                  onClick={() => {
                    onChange(opt.value)
                    setOpen(false)
                  }}
                >
                  {opt.iconUrl && (
                    <img src={opt.iconUrl} alt='' className='filter-select-option-icon' />
                  )}
                  {opt.label}
                </button>
              ))}
            </div>
          )
          return portal && !isUnder ? createPortal(menu, document.body) : menu
        })()}
    </div>
  )
}

export default FilterSelect
